<template>
  <div class="admin-page">
    <admin-page-header
      eyebrow="Yönetim"
      title="Başarımlar"
      description="Başarım tanımlarını, ödül puanlarını ve tekrar hesaplama akışını current backend kontratıyla yönetin."
    >
      <template #actions>
        <v-btn
          outlined
          color="primary"
          small
          :loading="recomputeLoading"
          @click="recomputeDialog = true"
        >
          <v-icon left>
            {{ icons.mdiRefreshCircle }}
          </v-icon>
          Yeniden Hesapla
        </v-btn>
        <v-btn
          outlined
          color="primary"
          small
          :loading="loading"
          @click="fetchAchievements"
        >
          <v-icon left>
            {{ icons.mdiRefresh }}
          </v-icon>
          Yenile
        </v-btn>
        <v-btn
          color="primary"
          small
          @click="openDialog()"
        >
          <v-icon left>
            {{ icons.mdiMedalOutline }}
          </v-icon>
          Yeni Başarım
        </v-btn>
      </template>
    </admin-page-header>

    <admin-metric-strip
      :items="summaryCards"
      :loading="loading"
    />

    <v-alert
      v-if="tableError"
      dense
      outlined
      type="error"
      class="mb-4"
    >
      {{ tableError }}
    </v-alert>

    <v-alert
      v-if="recomputeResult"
      dense
      outlined
      type="success"
      class="mb-4"
    >
      {{ recomputeSummary }}
    </v-alert>

    <v-card
      class="admin-table-card"
      outlined
    >
      <v-data-table
        dense
        :loading="loading"
        loading-text="Yükleniyor"
        :headers="headers"
        :items="achievements"
        item-key="id"
        disable-pagination
        hide-default-footer
        class="admin-data-table elevation-0"
      >
        <template v-slot:item.badge="{ item }">
          <v-avatar
            size="38"
            :style="{ background: item.bgColor || '#EEF2FF' }"
            class="my-2"
          >
            <v-img
              v-if="item.iconType === 'image' && item.iconName"
              :src="item.iconName"
              contain
            ></v-img>
            <v-icon
              v-else
              :color="item.color || '#2563eb'"
            >
              {{ resolveAchievementIcon(item.iconName, item.iconType) }}
            </v-icon>
          </v-avatar>
        </template>

        <template v-slot:item.triggerType="{ item }">
          <v-chip
            small
            outlined
            color="primary"
          >
            {{ triggerTypeLabel(item.triggerType) }}
          </v-chip>
        </template>

        <template v-slot:item.periodType="{ item }">
          <v-chip
            small
            outlined
          >
            {{ item.periodType || 'ONCE' }}
          </v-chip>
        </template>

        <template v-slot:item.rewardPoints="{ item }">
          <strong>{{ formatNumber(item.rewardPoints) }}</strong>
        </template>

        <template v-slot:item.sortOrder="{ item }">
          {{ displayAchievementPosition(item) }}
        </template>

        <template v-slot:item.active="{ item }">
          <v-chip
            small
            :color="item.active ? 'success' : 'grey'"
            dark
          >
            {{ item.active ? 'Aktif' : 'Pasif' }}
          </v-chip>
        </template>

        <template v-slot:item.modified="{ item }">
          {{ formatDate(item.modified || item.created) }}
        </template>

        <template v-slot:item.actions="{ item }">
          <div
            class="d-flex align-center"
            style="gap: 8px;"
          >
            <v-btn
              icon
              small
              color="primary"
              @click="openDialog(item)"
            >
              <v-icon>{{ icons.mdiPencil }}</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              color="error"
              @click="openDeleteDialog(item)"
            >
              <v-icon>{{ icons.mdiDelete }}</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog
      v-model="recomputeDialog"
      max-width="560"
      persistent
    >
      <admin-confirm-dialog
        title="Başarımları yeniden hesapla"
        description="Tüm uygun kullanıcılar yeniden değerlendirilecek. Yeni tamamlanan başarımlar için ödül puanı yazılabilir. Bu işlem yalnız önizleme değildir."
        confirm-text="Yeniden Hesapla"
        :save="recomputeAchievements"
        :close="() => { recomputeDialog = false }"
      />
    </v-dialog>
    <v-dialog
      v-model="dialog"
      max-width="980px"
      :persistent="saving || badgeImageUploading"
    >
      <v-card class="admin-dialog-card">
        <v-toolbar
          dense
          :elevation="0"
        >
          <v-toolbar-title>{{ formTitle }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="closeDialog"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-6">
          <v-alert
            v-if="tableError"
            type="error"
            dense
            outlined
          >
            {{ tableError }}
          </v-alert>
          <div class="achievement-form-preview mb-5">
            <v-img
              v-if="editedItem.iconType === 'image'"
              :src="badgeImagePreview || editedItem.iconName"
              max-width="64"
              height="64"
              contain
            />
            <v-icon
              v-else
              size="48"
              :color="editedItem.color"
            >
              {{ badgeIconPath }}
            </v-icon>
            <div>
              <strong>{{ editedItem.title || 'Başarım adı' }}</strong><p>{{ editedItem.description }}</p><div class="text-caption">
                Hedef: {{ formatNumber(editedItem.targetValue) }} {{ editedItem.unit }} · Ödül: {{ formatNumber(editedItem.rewardPoints) }} puan
              </div>
            </div>
          </div>
          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model="editedItem.title"
                outlined
                dense
                label="Başlık"
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model="editedItem.achievementKey"
                outlined
                dense
                label="Kod"
                placeholder="first-link"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="editedItem.description"
                outlined
                dense
                rows="3"
                label="Açıklama"
              ></v-textarea>
            </v-col>
          </v-row>

          <v-row>
            <v-col
              cols="12"
              md="3"
            >
              <v-select
                v-model="editedItem.triggerType"
                :items="triggerTypeOptions"
                item-text="text"
                item-value="value"
                outlined
                dense
                label="Tetik"
              ></v-select>
            </v-col>
            <v-col
              cols="12"
              md="3"
            >
              <v-select
                v-model="editedItem.periodType"
                :items="periodTypeOptions"
                outlined
                dense
                label="Periyot"
              ></v-select>
            </v-col>
            <v-col
              cols="12"
              md="3"
            >
              <v-select
                v-model="editedItem.category"
                :items="categoryOptions"
                outlined
                dense
                label="Kategori"
              ></v-select>
            </v-col>
            <v-col
              cols="12"
              md="3"
            >
              <v-select
                v-model="editedItem.rarity"
                :items="rarityOptions"
                outlined
                dense
                label="Seviye"
              ></v-select>
            </v-col>
          </v-row>

          <v-row>
            <v-col
              cols="12"
              md="3"
            >
              <v-text-field
                v-model.number="editedItem.targetValue"
                outlined
                dense
                type="number"
                label="Hedef"
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              md="3"
            >
              <v-text-field
                v-model.number="editedItem.rewardPoints"
                outlined
                dense
                type="number"
                label="Ödül Puanı"
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              md="3"
            >
              <v-text-field
                v-model="editedItem.unit"
                outlined
                dense
                label="Birim"
                placeholder="TL / link / ödül"
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              md="3"
            >
              <v-text-field
                v-model.number="editedItem.position"
                outlined
                dense
                type="number"
                label="Pozisyon"
                placeholder="1"
                hint="Arayüzde 1, 2, 3 olarak görünür; backend'de 10'luk adımlarla saklanır."
                persistent-hint
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col
              cols="12"
              md="4"
            >
              <v-text-field
                v-model="editedItem.iconName"
                outlined
                dense
                :label="editedItem.iconType === 'image' ? 'Rozet görsel URL' : 'Icon Name'"
                :placeholder="editedItem.iconType === 'image' ? 'https://...' : 'trophy-outline'"
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              md="4"
            >
              <v-select
                v-model="editedItem.iconType"
                :items="iconTypeOptions"
                item-text="text"
                item-value="value"
                outlined
                dense
                label="Rozet Tipi"
              ></v-select>
            </v-col>
            <v-col
              cols="12"
              md="4"
            >
              <v-switch
                v-model="editedItem.active"
                label="Aktif"
              ></v-switch>
            </v-col>
          </v-row>

          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <div class="d-flex align-center">
                <v-text-field
                  v-model="editedItem.color"
                  outlined
                  dense
                  label="İkon Rengi"
                  placeholder="#2563eb"
                  class="flex-grow-1"
                ></v-text-field>
                <v-menu
                  v-model="colorMenu"
                  offset-y
                  :close-on-content-click="false"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      class="ml-2"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <span :style="colorSwatchStyle(editedItem.color)"></span>
                    </v-btn>
                  </template>
                  <v-card class="pa-2">
                    <v-color-picker
                      v-model="editedItem.color"
                      mode="hexa"
                      show-swatches
                      hide-mode-switch
                      flat
                    ></v-color-picker>
                  </v-card>
                </v-menu>
              </div>
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <div class="d-flex align-center">
                <v-text-field
                  v-model="editedItem.bgColor"
                  outlined
                  dense
                  label="Arkaplan"
                  placeholder="#EEF2FF"
                  class="flex-grow-1"
                ></v-text-field>
                <v-menu
                  v-model="bgColorMenu"
                  offset-y
                  :close-on-content-click="false"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      class="ml-2"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <span :style="colorSwatchStyle(editedItem.bgColor)"></span>
                    </v-btn>
                  </template>
                  <v-card class="pa-2">
                    <v-color-picker
                      v-model="editedItem.bgColor"
                      mode="hexa"
                      show-swatches
                      hide-mode-switch
                      flat
                    ></v-color-picker>
                  </v-card>
                </v-menu>
              </div>
            </v-col>
          </v-row>

          <v-row v-if="editedItem.iconType === 'image'">
            <v-col
              cols="12"
              md="7"
            >
              <v-file-input
                v-model="badgeImageFile"
                accept="image/*"
                outlined
                dense
                :loading="badgeImageUploading"
                label="Rozet görseli yükle"
                prepend-icon=""
                prepend-inner-icon="mdi-image-plus"
                @change="onBadgeImageSelected"
              ></v-file-input>
            </v-col>
            <v-col
              cols="12"
              md="5"
            >
              <v-card
                v-if="editedItem.iconType === 'image' && (badgeImagePreview || editedItem.iconName)"
                outlined
                class="admin-surface pa-3"
              >
                <v-img
                  :src="badgeImagePreview || editedItem.iconName"
                  height="120"
                  contain
                ></v-img>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            color="primary"
            @click="closeDialog"
          >
            İptal
          </v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            @click="saveAchievement"
          >
            Kaydet
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="deleteDialog"
      :persistent="deleteLoading"
      max-width="480px"
    >
      <v-card class="admin-dialog-card">
        <v-toolbar
          dense
          :elevation="0"
        >
          <v-toolbar-title>Başarımı Sil</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="closeDeleteDialog"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-6">
          <v-alert
            v-if="tableError"
            type="error"
            dense
            outlined
          >
            {{ tableError }}
          </v-alert>
          <p class="mb-0">
            <strong>{{ deleteItem.title || deleteItem.achievementKey }}</strong> başarımını silmek istediğine emin misin?
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            color="primary"
            @click="closeDeleteDialog"
          >
            İptal
          </v-btn>
          <v-btn
            color="error"
            :loading="deleteLoading"
            @click="confirmDelete"
          >
            Sil
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import * as icons from '@mdi/js'
import { getAccessToken } from '@/utils/panelSession'
import AdminPageHeader from '@/components/admin/AdminPageHeader.vue'
import AdminMetricStrip from '@/components/admin/AdminMetricStrip.vue'
import AdminConfirmDialog from '@/components/admin/AdminConfirmDialog.vue'
import { resolveApiErrorMessage } from '@/utils/apiErrors'

const defaultAchievement = () => ({
  id: null,
  achievementKey: '',
  title: '',
  description: '',
  triggerType: 'LINK_COUNT',
  periodType: 'ONCE',
  category: 'LINK',
  rarity: 'BRONZE',
  targetValue: 0,
  rewardPoints: 0,
  unit: '',
  iconName: 'trophy-outline',
  iconType: 'materialcomm',
  color: '#2563eb',
  bgColor: '#EEF2FF',
  active: true,
  sortOrder: 0,
  position: 1,
  created: null,
  modified: null,
})

export default {
  name: 'AchievementsManagement',
  components: {
    AdminPageHeader,
    AdminMetricStrip,
    AdminConfirmDialog,
  },
  data: () => ({
    icons,
    achievements: [],
    loading: false,
    saving: false,
    deleteLoading: false,
    recomputeLoading: false,
    recomputeDialog: false,
    badgeImageUploading: false,
    dialog: false,
    deleteDialog: false,
    colorMenu: false,
    bgColorMenu: false,
    badgeImagePreview: '',
    badgeImageFile: null,
    tableError: '',
    recomputeResult: null,
    editedIndex: null,
    editedItem: defaultAchievement(),
    deleteItem: defaultAchievement(),
    headers: [
      {
        text: 'İşlemler', value: 'actions', sortable: false, width: 120,
      },
      {
        text: 'Rozet', value: 'badge', sortable: false, width: 90,
      },
      { text: 'Başlık', value: 'title' },
      { text: 'Kod', value: 'achievementKey', width: 180 },
      { text: 'Tetik', value: 'triggerType', width: 170 },
      { text: 'Periyot', value: 'periodType', width: 120 },
      { text: 'Hedef', value: 'targetValue', width: 120 },
      { text: 'Ödül', value: 'rewardPoints', width: 120 },
      { text: 'Durum', value: 'active', width: 110 },
      { text: 'Pozisyon', value: 'sortOrder', width: 100 },
      { text: 'Güncelleme', value: 'modified', width: 180 },
    ],
    triggerTypeOptions: [
      { text: 'Link Sayısı', value: 'LINK_COUNT' },
      { text: 'Toplam Tıklama', value: 'CLICK_TOTAL' },
      { text: 'Toplam Kazanç', value: 'EARNING_TOTAL' },
      { text: 'İlk Satış', value: 'FIRST_SALE' },
      { text: 'Premium Aktif', value: 'PREMIUM_ACTIVE' },
      { text: 'Başarım Sayısı', value: 'ACHIEVEMENT_COUNT' },
      { text: 'Login Serisi', value: 'LOGIN_STREAK' },
    ],
    periodTypeOptions: ['ONCE', 'WEEKLY', 'MONTHLY'],
    categoryOptions: ['LINK', 'CLICK', 'EARNING', 'BRAND', 'SPECIAL'],
    rarityOptions: ['BRONZE', 'SILVER', 'GOLD'],
    iconTypeOptions: [
      { text: 'Feather icon', value: 'feather' },
      { text: 'Material Community', value: 'materialcomm' },
      { text: 'Görsel rozet', value: 'image' },
    ],
  }),
  computed: {
    badgeIconPath() {
      const key = `mdi${String(this.editedItem.iconName || '').replace(/(^|-)(\w)/g, (_, dash, letter) => letter.toUpperCase())}`

      return icons[key] || icons.mdiTrophyOutline
    },
    requestOptions() {
      const token = getAccessToken()

      return {
        headers: { Authorization: `Bearer ${token}` },
      }
    },
    formTitle() {
      return this.editedIndex ? 'Başarımı Güncelle' : 'Yeni Başarım'
    },
    recomputeSummary() {
      if (!this.recomputeResult) return ''

      return `Tarama tamamlandı. Kullanıcı: ${this.recomputeResult.usersEvaluated}, yeni state: ${this.recomputeResult.statesCreated}, güncellenen state: ${this.recomputeResult.statesUpdated}, yeni tamamlanan: ${this.recomputeResult.newlyCompleted}, ödül yazılan: ${this.recomputeResult.rewardsGranted}.`
    },
    summaryCards() {
      const activeCount = this.achievements.filter(item => item.active !== false).length
      const totalReward = this.achievements.reduce((sum, item) => sum + Number(item.rewardPoints || 0), 0)
      const recurringCount = this.achievements.filter(item => item.periodType !== 'ONCE').length

      return [
        {
          label: 'Toplam kayıt',
          value: this.achievements.length,
          helper: 'Panelde görünen başarım tanımı',
          icon: icons.mdiMedalOutline,
          color: '#2563eb',
          softColor: '#dbeafe',
        },
        {
          label: 'Aktif başarı',
          value: activeCount,
          helper: 'Canlıda hesaplanan kurallar',
          icon: icons.mdiCheckCircleOutline,
          color: '#16a34a',
          softColor: '#dcfce7',
        },
        {
          label: 'Toplam ödül',
          value: this.formatNumber(totalReward),
          helper: 'Advp puan toplamı',
          icon: icons.mdiStarOutline,
          color: '#ea580c',
          softColor: '#ffedd5',
        },
        {
          label: 'Tekrarlı başarı',
          value: recurringCount,
          helper: 'Weekly / Monthly reset',
          icon: icons.mdiCalendarRefresh,
          color: '#7c3aed',
          softColor: '#ede9fe',
        },
      ]
    },
  },
  mounted() {
    this.fetchAchievements()
  },
  methods: {
    normalizeList(payload) {
      if (Array.isArray(payload)) return payload
      if (Array.isArray(payload?.list)) return payload.list
      if (Array.isArray(payload?.content)) return payload.content
      if (Array.isArray(payload?.items)) return payload.items

      return []
    },
    triggerTypeLabel(value) {
      return this.triggerTypeOptions.find(option => option.value === value)?.text || value || '-'
    },
    iconTypeLabel(value) {
      return this.iconTypeOptions.find(option => option.value === value)?.text || value || '-'
    },
    displayAchievementPosition(item) {
      const sortOrder = Number(item?.sortOrder || 0)
      if (!sortOrder) return '-'

      return Math.max(1, Math.round(sortOrder / 10))
    },
    colorSwatchStyle(color) {
      return {
        width: '18px',
        height: '18px',
        borderRadius: '999px',
        display: 'inline-block',
        border: '1px solid rgba(148, 163, 184, 0.45)',
        backgroundColor: color || '#2563eb',
      }
    },
    resolveAchievementIcon(iconName) {
      const normalized = String(iconName || '').toLowerCase()

      if (normalized.includes('crown')) return icons.mdiCrownOutline
      if (normalized.includes('gift')) return icons.mdiGiftOutline
      if (normalized.includes('flame')) return icons.mdiFire
      if (normalized.includes('star')) return icons.mdiStarOutline
      if (normalized.includes('mouse')) return icons.mdiCursorDefaultClickOutline
      if (normalized.includes('trending') || normalized.includes('dollar')) return icons.mdiTrendingUp
      if (normalized.includes('target')) return icons.mdiTarget
      if (normalized.includes('link')) return icons.mdiLinkVariant

      return icons.mdiTrophyOutline
    },
    async uploadAchievementBadge(file) {
      if (!file) return ''

      this.badgeImageUploading = true
      try {
        const formData = new FormData()
        formData.append('file', file)
        const response = await axios.post(
          `${process.env.VUE_APP_BASE_API_URL2}file/uploadFile`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${getAccessToken()}`,
              'Content-Type': 'multipart/form-data',
            },
          },
        )

        return response?.data?.localUrl
          || response?.data?.data?.localUrl
          || response?.data?.url
          || response?.data?.data?.url
          || ''
      } finally {
        this.badgeImageUploading = false
      }
    },
    async onBadgeImageSelected(file) {
      if (!file) {
        this.badgeImagePreview = ''

        return
      }

      try {
        const imageUrl = await this.uploadAchievementBadge(file)
        if (!imageUrl) {
          throw new Error('Rozet görsel URL alınamadı.')
        }

        this.badgeImagePreview = imageUrl
        this.editedItem.iconType = 'image'
        this.editedItem.iconName = imageUrl
      } catch (error) {
        this.tableError = 'Rozet görseli yüklenemedi.'
        console.error('Achievement badge upload hatası:', error)
      }
    },
    async fetchAchievements() {
      this.loading = true
      this.tableError = ''
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API_URL}admin/achievements`,
          this.requestOptions,
        )
        const payload = response?.data?.data ?? response?.data
        this.achievements = this.normalizeList(payload).map(item => ({
          ...defaultAchievement(),
          ...item,
          active: item.active !== false,
        }))
      } catch (error) {
        this.tableError = 'Başarım listesi yüklenemedi.'
        console.error('Achievements yüklenirken hata oluştu:', error)
      } finally {
        this.loading = false
      }
    },
    async recomputeAchievements() {
      if (this.recomputeLoading) return false
      this.recomputeLoading = true
      this.tableError = ''
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_BASE_API_URL}admin/achievements/recompute`,
          {},
          this.requestOptions,
        )
        this.recomputeResult = response?.data?.data ?? null
        await this.fetchAchievements()

        return true
      } catch (error) {
        throw new Error(resolveApiErrorMessage(error, 'Başarımlar yeniden hesaplanamadı.'))
      } finally {
        this.recomputeLoading = false
      }
    },
    formatDate(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp)
      if (Number.isNaN(date.getTime())) return '-'

      return date.toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    formatNumber(value) {
      const parsed = Number(value || 0)

      return new Intl.NumberFormat('tr-TR').format(parsed)
    },
    openDialog(item = null) {
      this.recomputeResult = null
      if (item) {
        this.editedIndex = item.id
        this.editedItem = {
          ...defaultAchievement(),
          ...item,
          active: item.active !== false,
          position: Math.max(1, Math.round(Number(item.sortOrder || 0) / 10)) || 1,
        }
      } else {
        this.editedIndex = null
        this.editedItem = defaultAchievement()
      }
      this.badgeImagePreview = this.editedItem.iconType === 'image' ? this.editedItem.iconName : ''
      this.badgeImageFile = null
      this.colorMenu = false
      this.bgColorMenu = false
      this.dialog = true
    },
    closeDialog() {
      if (this.saving || this.badgeImageUploading) return
      this.dialog = false
      setTimeout(() => {
        if (this.dialog) return
        this.editedIndex = null
        this.editedItem = defaultAchievement()
        this.badgeImagePreview = ''
        this.badgeImageFile = null
        this.colorMenu = false
        this.bgColorMenu = false
      }, 200)
    },
    async saveAchievement() {
      if (this.saving || this.badgeImageUploading) return
      if (!String(this.editedItem.title || '').trim() || !String(this.editedItem.achievementKey || '').trim()) {
        this.tableError = 'Başarım adı ve anahtarı zorunlu.'

        return
      }
      if (![this.editedItem.targetValue, this.editedItem.rewardPoints].every(value => Number.isFinite(Number(value)) && Number(value) >= 0)) {
        this.tableError = 'Hedef ve ödül negatif olamaz.'

        return
      }
      this.saving = true
      this.tableError = ''
      try {
        const payload = {
          achievementKey: this.editedItem.achievementKey,
          title: this.editedItem.title,
          description: this.editedItem.description,
          triggerType: this.editedItem.triggerType,
          periodType: this.editedItem.periodType,
          category: this.editedItem.category,
          rarity: this.editedItem.rarity,
          targetValue: Number(this.editedItem.targetValue || 0),
          rewardPoints: Number(this.editedItem.rewardPoints || 0),
          unit: this.editedItem.unit || '',
          iconName: this.editedItem.iconName || '',
          iconType: this.editedItem.iconType || '',
          color: this.editedItem.color || '',
          bgColor: this.editedItem.bgColor || '',
          active: !!this.editedItem.active,
          sortOrder: Number(this.editedItem.position || 0) * 10,
        }

        if (this.editedIndex) {
          await axios.put(
            `${process.env.VUE_APP_BASE_API_URL}admin/achievements/${this.editedIndex}`,
            payload,
            this.requestOptions,
          )
        } else {
          await axios.post(
            `${process.env.VUE_APP_BASE_API_URL}admin/achievements`,
            payload,
            this.requestOptions,
          )
        }

        this.saving = false
        this.closeDialog()
        await this.fetchAchievements()
      } catch (error) {
        this.tableError = 'Başarım kaydedilemedi.'
        console.error('Achievement kaydedilirken hata oluştu:', error)
      } finally {
        this.saving = false
      }
    },
    openDeleteDialog(item) {
      this.tableError = ''
      this.deleteItem = { ...item }
      this.deleteDialog = true
    },
    closeDeleteDialog() {
      if (this.deleteLoading) return
      this.deleteDialog = false
      this.deleteItem = defaultAchievement()
    },
    async confirmDelete() {
      if (!this.deleteItem?.id || this.deleteLoading) return

      this.deleteLoading = true
      this.tableError = ''
      try {
        await axios.delete(
          `${process.env.VUE_APP_BASE_API_URL}admin/achievements/${this.deleteItem.id}`,
          this.requestOptions,
        )
        this.deleteLoading = false
        this.closeDeleteDialog()
        await this.fetchAchievements()
      } catch (error) {
        this.tableError = 'Başarım silinemedi.'
        console.error('Achievement silinirken hata oluştu:', error)
      } finally {
        this.deleteLoading = false
      }
    },
  },
}
</script>
<style scoped>
.achievement-form-preview { display: flex; align-items: center; gap: 16px; padding: 16px 0; border-bottom: 1px solid var(--admin-border); }
.achievement-form-preview > div { min-width: 0; overflow-wrap: anywhere; }
.achievement-form-preview p { margin: 4px 0 8px; }
</style>
